import {
    ActionIcon,
    Button,
    Card,
    Fieldset,
    Group,
    MultiSelect,
    NumberInput,
    Select,
    Stack,
    Text,
    Tooltip,
} from '@mantine/core';
import { type UseFormReturnType } from '@mantine/form';
import { IconInfoCircle, IconPlus, IconTrash } from '@tabler/icons-react';
import { banks } from '~/entities/bank';
import { currencies } from '~/entities/currency';
import { markets } from '~/entities/market';
import { type PayinPaymentChannel, type PayinTraderConfig } from '~/entities/payin';
import { paymentMethods } from '~/entities/paymentMehtod';
import { requisiteTypes } from '~/entities/requisiteType';
import { UniteTypeCurrency, UniteTypePaymentMethod, UniteTypePaymentType } from '~/generated/payoutBackend/model';

interface ConfigEditorProps<T extends { config?: PayinTraderConfig }> {
    form: UseFormReturnType<T>;
    isLoading?: boolean;
}

const banksTooltip =
    'Укажите список банков для разрешения и/или запрета. Если список разрешённых пуст — доступны все банки, кроме указанных в запрещённых. Если список разрешённых заполнен — доступны только банки из него, за исключением банков из запрещённого списка. Запрещённые банки всегда имеют приоритет.';

export const ConfigEditor = <T extends { config?: PayinTraderConfig }>({ form, isLoading }: ConfigEditorProps<T>) => {
    const addPaymentChannel = () => {
        const value = {
            config: {
                maxQueueLength: '',
            },
            filter: {
                amount: {
                    min: { value: '' },
                    max: { value: '' },
                },
                allowedBanks: [],
                forbiddenBanks: [],
                currency: UniteTypeCurrency.CURRENCY_UNSPECIFIED,
                method: UniteTypePaymentMethod.METHOD_UNSPECIFIED,
                type: UniteTypePaymentType.TYPE_UNSPECIFIED,
            },
        } satisfies PayinPaymentChannel;

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        form.insertListItem('config.paymentChannels', value as any);
    };

    const removePaymentChannel = (index: number) => {
        form.removeListItem('config.paymentChannels', index);
    };

    return (
        <Fieldset legend='Конфигурация'>
            <Stack gap='md'>
                <NumberInput
                    label='Лимит'
                    placeholder='Введите число'
                    valueIsNumericString
                    min={0}
                    {...form.getInputProps('config.limit.value')}
                />

                <MultiSelect
                    searchable
                    label='Площадка'
                    placeholder='Выберите из списка'
                    data={markets}
                    {...form.getInputProps('config.allowedMarkets')}
                />

                {form.values.config?.paymentChannels?.map((_, index) => (
                    <Card
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        padding='md'
                        withBorder
                        radius='md'
                    >
                        <Group
                            justify='space-between'
                            mb='md'
                        >
                            <Text fw={500}>Канал #{index + 1}</Text>
                            <ActionIcon
                                size='lg'
                                color='red'
                                variant='subtle'
                                onClick={() => removePaymentChannel(index)}
                                aria-label='Удалить канал'
                            >
                                <IconTrash size={18} />
                            </ActionIcon>
                        </Group>

                        <Stack gap='xs'>
                            <Group grow>
                                <Select
                                    label='Валюта'
                                    data={currencies}
                                    placeholder='Выберите валюту'
                                    searchable
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.currency`)}
                                    clearable
                                />
                                <Select
                                    label='Метод'
                                    data={paymentMethods}
                                    placeholder='Выберите метод'
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.method`)}
                                    clearable
                                />
                                <Select
                                    label='Тип реквизита'
                                    placeholder='Выберите тип'
                                    data={requisiteTypes}
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.type`)}
                                    clearable
                                />
                            </Group>

                            <Group
                                align='end'
                                gap='xs'
                            >
                                <Group
                                    grow
                                    flex={1}
                                    align='center'
                                >
                                    <MultiSelect
                                        label='Разрешенные банки'
                                        data={banks}
                                        placeholder='Выберите банк'
                                        {...form.getInputProps(`config.paymentChannels.${index}.filter.allowedBanks`)}
                                        clearable
                                        searchable
                                    />

                                    <MultiSelect
                                        label='Запрещённые банки'
                                        data={banks}
                                        placeholder='Выберите банк'
                                        {...form.getInputProps(`config.paymentChannels.${index}.filter.forbiddenBanks`)}
                                        clearable
                                        searchable
                                    />
                                </Group>

                                <div>
                                    <Tooltip
                                        label={banksTooltip}
                                        maw={280}
                                        position='right'
                                        multiline
                                        withArrow
                                    >
                                        <IconInfoCircle size={18} />
                                    </Tooltip>
                                </div>
                            </Group>

                            <Group grow>
                                <NumberInput
                                    label='Мин. сумма'
                                    min={0}
                                    placeholder='Введите число'
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.amount.min.value`)}
                                />
                                <NumberInput
                                    label='Макс. сумма'
                                    min={0}
                                    placeholder='Введите число'
                                    {...form.getInputProps(`config.paymentChannels.${index}.filter.amount.max.value`)}
                                />
                            </Group>

                            <NumberInput
                                label='Макс. длина очереди'
                                placeholder='Введите число'
                                min={0}
                                {...form.getInputProps(`config.paymentChannels.${index}.config.maxQueueLength`)}
                            />
                        </Stack>
                    </Card>
                ))}

                {(!form.values.config?.paymentChannels || form.values.config.paymentChannels.length === 0) && (
                    <Text
                        c='dimmed'
                        size='sm'
                    >
                        Нет добавленных каналов
                    </Text>
                )}

                <Button
                    variant='subtle'
                    leftSection={<IconPlus size={16} />}
                    onClick={addPaymentChannel}
                    size='xs'
                    disabled={isLoading}
                    loading={isLoading}
                >
                    Добавить канал
                </Button>
            </Stack>
        </Fieldset>
    );
};
